import { useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, TrendingDown, ChevronRight } from 'lucide-react';
import { useTransactionsQuery } from '../hooks/useQueries';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Format date as YYYY-MM-DD in local timezone (not UTC)
function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export default function BudgetCategoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { formatAmount, formatDate, formatTime, config } = useConfig();
  const { hasEncryptionKey } = useAuth();
  const { isModernUI } = useTheme();

  const category = location.state?.category;
  const categoryName = category?.name;

  // Build transaction query for the budget category
  const transactionParams = useMemo(() => {
    if (!categoryName) return null;
    const now = new Date();
    const start = new Date(now);
    start.setDate(start.getDate() - 29);
    return {
      category: category?._id || categoryName,
      type: 'expense',
      startDate: formatLocalDate(start),
      endDate: formatLocalDate(now)
    };
  }, [categoryName, location.key]);

  const { data: transactionData, isLoading } = useTransactionsQuery(
    transactionParams || {},
    { enabled: !!categoryName && hasEncryptionKey, placeholderData: undefined }
  );

  // If no category data, go back
  if (!category) {
    return (
      <div className="text-center py-12">
        <p className="text-surface-500">No category selected</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-primary-600 font-medium"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={() => navigate(-1)}
          className={isModernUI
            ? "p-1.5 rounded-lg bg-surface-100 hover:bg-surface-200 transition-colors"
            : "p-2 text-surface-600 hover:text-surface-900 rounded-lg transition-colors"
          }
        >
          <ArrowLeft className={isModernUI ? "w-4 h-4" : "w-5 h-5"} />
        </button>
        <h1 className="text-lg font-bold text-surface-900">{category.name}</h1>
      </div>

      {/* Budget Summary Card */}
      <div
        className="p-4 rounded-xl mb-4"
        style={{
          backgroundColor: category.isOverBudget ? '#fef2f2' : '#fffbeb',
          borderLeft: `4px solid ${category.isOverBudget ? '#ef4444' : '#f59e0b'}`
        }}
      >
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: category.color }}
          />
          <span className="font-medium text-surface-900">{category.name}</span>
          {category.isOverBudget && (
            <AlertTriangle className="w-4 h-4 text-red-500" />
          )}
        </div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-surface-600">
            {category.count} transactions
          </span>
          <span
            className="font-semibold"
            style={{ color: category.isOverBudget ? '#ef4444' : '#f59e0b' }}
          >
            {formatAmount(category.spent)} / {formatAmount(category.budget)}
            <span className="text-surface-400 ml-1">
              /{category.budgetPeriod === 'monthly' ? 'mo' : category.budgetPeriod === 'quarterly' ? 'qtr' : 'yr'}
            </span>
          </span>
        </div>
        <div className="h-2 bg-white/50 rounded-full overflow-hidden mb-2">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(category.percentage, 100)}%`,
              backgroundColor: category.isOverBudget ? '#ef4444' : '#f59e0b'
            }}
          />
        </div>
        <p
          className="text-sm font-medium"
          style={{ color: category.isOverBudget ? '#dc2626' : '#d97706' }}
        >
          {category.isOverBudget
            ? `Over budget by ${formatAmount(category.spent - category.budget)}! Try to reduce spending.`
            : `${formatAmount(category.budget - category.spent)} remaining. You're at ${category.percentage}% - consider reducing expenses.`
          }
        </p>
      </div>

      {/* Transactions List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : transactionData?.transactions?.length > 0 ? (
        <div className="border border-surface-200 rounded-xl divide-y divide-surface-100">
          {transactionData.transactions.map((tx) => (
            <button
              key={tx._id}
              onClick={() => navigate(`/transactions/${tx._id}`)}
              className="w-full flex items-center gap-3 p-3 hover:bg-surface-50 active:bg-surface-100 transition-colors text-left"
            >
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${config.expenseColor}15` }}
              >
                <TrendingDown className="w-4 h-4" style={{ color: config.expenseColor }} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-surface-900 truncate">
                  {tx.description || tx.category?.name || tx.category}
                </p>
                <p className="text-xs text-surface-500 truncate">
                  {formatDate(tx.date)}{tx.time && `, ${formatTime(tx.time)}`} • {tx.account?.name || tx.bankName}
                </p>
              </div>
              <span
                className="text-sm font-bold flex-shrink-0"
                style={{ color: config.expenseColor }}
              >
                {formatAmount(tx.amount || tx.amountInINR)}
              </span>
              <ChevronRight className="w-4 h-4 text-surface-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-surface-500">No transactions found</p>
        </div>
      )}
    </div>
  );
}
